/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Count Down
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       27th July 2021

*******************************************************************************************/
import React            from 'react';
import { styled, Box }  from '@mui/material';
import moment           from 'moment';
import { useClock }     from 'hooks';

/**
 * A no-operation function.
 * 
 * This function does nothing when invoked. It is often used as a placeholder
 * or a default function when no operation is desired.
 *
 * @returns {void} This function does not return anything.
 *
 * @example
 * noop(); // Does nothing
 */
const noop = () => {};

/**
 * Pads a number with leading zeros to ensure it has at least two digits.
 * If the input is falsy (e.g., `null`, `undefined`, or `0`), it is treated as `0`.
 * The absolute value of the number is taken before padding.
 *
 * @param {number} x - The number to be padded.
 * @returns {string} The number as a string, padded with leading zeros if necessary.
 * 
 * @example
 * absPad(5); // Returns "05"
 * absPad(10); // Returns "10"
 * absPad(-3); // Returns "03"
 * absPad(0); // Returns "00"
 */
const absPad = x => Math.abs(x || 0).toString().padStart(2,'0');

/**
 * Formatter function to generate a countdown string in the format `dd:hh:mm:ss`.
 *
 * @param {Object} params - The parameters for formatting.
 * @param {string} [params.prefix=''] - A prefix for the formatted string.
 * @param {number} [params.days=0] - The number of days.
 * @param {number} [params.hours=0] - The number of hours.
 * @param {number} [params.minutes=0] - The number of minutes.
 * @param {number} [params.seconds=0] - The number of seconds.
 * 
 * @returns {string} - The formatted string.
 */
const formatterFunction = ({
    prefix  = '',
    days    = 0,
    hours   = 0,
    minutes = 0,
    seconds = 0
} = {}) => (
    `${prefix}${[days,hours,minutes,seconds].map(absPad).join(':')}`
);

const   DEFAULT_FINISHING_SECONDS   = 60,
        DEFAULT_ALLOW_NEGATIVE      = false,
        DEFAULT_ON_EXPIRE           = noop,
        DEFAULT_FORCE               = false,
        DEFAULT_FORMATTER           = formatterFunction,
        DEFAULT_PREFIX              = undefined,
        DEFAULT_FINISHED_VALUE      = undefined,
        DEFAULT_PERIOD              = 250;

/**
 * Custom hook for countdown timer.
 *
 * @param {Object} options - The options for the countdown.
 * @param {moment.Moment} options.to - The target moment in time for the countdown.
 * @param {moment.Moment} [options.nowIn] - The current moment in time (optional).
 * @param {number} [options.finishingSeconds=DEFAULT_FINISHING_SECONDS] - The number of seconds before the countdown is considered finished.
 * @param {boolean} [options.allowNegative=DEFAULT_ALLOW_NEGATIVE] - Whether negative durations are allowed.
 * @param {Function} [options.onExpire=DEFAULT_ON_EXPIRE] - Callback function to run when the countdown expires.
 * @param {Function} [options.formatter=DEFAULT_FORMATTER] - Callback function to format the countdown duration.
 * @param {number} [options.period=DEFAULT_PERIOD] - The period for the clock.
 *
 * @throws {Error} If "to" is not a valid moment object.
 * @throws {Error} If "now" is provided but is not a valid moment object.
 *
 * @returns {Object} The countdown state, including:
 *   - {string | null} result: The formatted duration.
 *   - {boolean} finished: Whether the countdown has finished.
 *   - {boolean} isFinishing: Whether the countdown is nearing completion.
 *   - {moment.Duration | undefined} duration: The current countdown duration.
 */
export const useCountDown = ({
    to,
    now : nowIn                 = undefined,
    finishingSeconds            = DEFAULT_FINISHING_SECONDS,
    allowNegative               = DEFAULT_ALLOW_NEGATIVE,
    onExpire    : handleExpire  = DEFAULT_ON_EXPIRE,
    formatter   : handleFormat  = DEFAULT_FORMATTER,
    period                      = DEFAULT_PERIOD,
}) => {

    // Check
    if(!to || !moment.isMoment(to) || !to.isValid())
        throw new Error('to is required and must be a valid moment');
    if(nowIn && (!moment.isMoment(nowIn) || !nowIn.isValid()))
        throw new Error('if now is provided, it must be a valid moment')
    if(isNaN(period) || period < 0)
        throw new Error('period must be positive numeric')
    if(typeof handleExpire !== 'function')
        throw new Error('onExpire must be a function');
    if(typeof handleFormat !== 'function')
        throw new Error('formatter must be a function');

    const {now: nowLoc}                 = useClock(period);
    const now                           = React.useMemo(() => moment.isMoment(nowIn) ? nowIn : nowLoc, [nowIn, nowLoc]);

    // The Duration
    const [duration,    setDuration]    = React.useState(undefined);

    // CountDown
    React.useEffect(() => {
            
        // Difference
        const delta = to.diff(now);

        // Callback
        if(delta <= 0 && delta >= -Math.abs(period)){
            try {
                handleExpire();    
            } catch (err) {
                console.error("Error handling expire event",err);
            }   
        }

        // If negatives not allowed, then lower bound at zero
        const adjustedDelta = allowNegative ? delta : Math.max(delta,0); 

        // Convert to duration
        const newDuration = moment.duration(adjustedDelta, 'milliseconds')

        // Set state duration variable
        setDuration(newDuration);
        
    },[allowNegative, now, handleExpire, period, to])

    // Function to format the duration
    const formatDuration = React.useCallback((duration) => {

        // Not a duration
        if(!moment.isDuration(duration)) 
            return null;

        // Is duration in past?
        const isNeg = duration.asMilliseconds() < 0;

        // Build the various elements
        const   days    = Math.floor(duration.asDays()),
                hours   = duration.hours(),
                minutes = duration.minutes(),
                seconds = duration.seconds();

        // The Prefix
        const prefix = isNeg ? '-' : ''
        
        // Now build the formatted duration
        try {
            return handleFormat({ prefix, days, hours, minutes, seconds, duration});    
        } catch (err) {
            console.error("Error formatting the duration",err);   
        }
    },[handleFormat])

    // Set the result when duration/formatDuration changes
    const result = React.useMemo(() => formatDuration(duration), [formatDuration, duration]);

    // Use a memo to determine if countdown is finished
    const finished                      = React.useMemo(() => (
        moment.isDuration(duration) && 
        duration.asSeconds() <= 0
    ), [duration]);

    // Is Finishing or Not
    const isFinishing = React.useMemo(() => (
        moment.isDuration(duration) && 
        duration.asSeconds() <= finishingSeconds
    ), [duration, finishingSeconds]);

    return {
        result,
        finished,
        isFinishing,
        duration
    }
};

const TimerContainer = styled(Box,{
    shouldForwardProp : prop => prop !== 'expiring'
})(({theme, expiring = false}) => ({
    width : '100%',
    ...(expiring && {
        color       : theme.palette.error.main,
        fontWeight  : 800
    })
}));

/**
 * A countdown timer component that uses `useCountDown` to manage the countdown state.
 * It renders the formatted countdown result and handles custom expiration behavior.
 *
 * @param {Object} props - The props for the countdown component.
 * @param {moment.Moment} props.to - The target moment in time for the countdown.
 * @param {moment.Moment} [props.now] - The current moment in time (optional).
 * @param {number} [props.finishingSeconds=DEFAULT_FINISHING_SECONDS] - The number of seconds before the countdown is considered finished.
 * @param {boolean} [props.allowNegative=DEFAULT_ALLOW_NEGATIVE] - Whether negative durations are allowed.
 * @param {Function} [props.onExpire=DEFAULT_ON_EXPIRE] - Callback function to run when the countdown expires.
 * @param {boolean} [props.force=DEFAULT_FORCE] - Force a re-render of the component (optional).
 * @param {Function} [props.formatter=DEFAULT_FORMATTER] - Callback function to format the countdown duration.
 * @param {string} [props.prefix=DEFAULT_PREFIX] - The prefix to display before the countdown result.
 * @param {string} [props.finishedValue=DEFAULT_FINISHED_VALUE] - The value to display when the countdown has finished.
 * @param {number} [props.period=DEFAULT_PERIOD] - The period for the clock.
 * @param {Function} [props.render] - Optional custom render function that receives countdown information.
 * 
 * @returns {JSX.Element} The rendered countdown component.
 *
 * @example
 * <CountDown
 *   to={moment().add(5, 'minutes')}
 *   now={moment()}
 *   onExpire={() => alert('Countdown expired!')}
 * />
 */
export const CountDown = ({
    to,
    now                 = undefined,
    finishingSeconds    = DEFAULT_FINISHING_SECONDS,
    allowNegative       = DEFAULT_ALLOW_NEGATIVE,
    onExpire            = DEFAULT_ON_EXPIRE,
    force               = DEFAULT_FORCE,
    formatter           = DEFAULT_FORMATTER,
    prefix              = DEFAULT_PREFIX,
    finishedValue       = DEFAULT_FINISHED_VALUE,
    period              = DEFAULT_PERIOD,
    ...props
}) => {
    const {render, ...rest} = props;
    const {result : countDown,finished,isFinishing,duration} = useCountDown({ 
        to, 
        now,
        finishingSeconds, 
        allowNegative, 
        onExpire, 
        formatter, 
        period 
    })
    // Done, Render
    return (
        <TimerContainer component="span" {...rest} expiring={isFinishing}>
            { 
                render && 
                render({countDown, duration, isFinishing, finished})}
            {
                !render && (!finished || !finishedValue) &&
                <>
                    {prefix} {countDown}
                </>
            }
            {
                !render && (finished && finishedValue) &&
                <>
                    {finishedValue}
                </>
            }
        </TimerContainer>
    )
}

export default CountDown;